import { useEffect } from "react";
import { Redirect, Router } from "@reach/router";
import { connect } from "react-redux";
import Skeleton from "react-loading-skeleton";
import { ContentContainer } from "../components/Layout";
import { AppMenu } from "newui/features/AppMenu";
import {
	getDocuments,
	getPastProjects,
	getResources,
} from "actions/companyActions";
//Routes
import { MyAccount } from "../routes/account/MyAccount";
import { Breadcrumbs } from "components/Breadcrumb";

import { NotFound } from "newui/features/ErrorPages/NotFound";
import {
	PdfSettingsPage,
	RfxSettings,
	Settings,
	RolesPermissionsPage,
	AuditTrailPage,
} from "newui/features/Settings";
import { AddProject } from "components/ProjectForm/AddProject";
import { EditProject } from "components/ProjectForm/EditProject";
import { DocumentRequest } from "components/DocumentRequest";
import { UpdateCreditCard } from "components/CreditCardDetail";

import {
	CurrentAccountType,
	useProfileContext,
} from "newui/features/Auth/context/ProfileContext";
import { RfxBidderTendersQuotesPage } from "newui/features/Rfx/pages/RfxBidderTendersQuotesPage";
import { RfxApprovalsPage } from "newui/features/Rfx/pages/RfxApprovalsPage";
import { RfxPanelPage } from "newui/features/Rfx/pages/RfxPanelPage";
import { RfxReadOnlyPreviewPage } from "newui/features/Rfx/pages/RfxReadOnlyPreviewPage";
import { Search } from "./search/Search";
import { Invoice } from "components/Invoice";
import { EditUserAccount } from "components/EditUserAccount";
import { Box } from "@chakra-ui/react";
import RFQPreview from "newui/features/Rfx/Submission/RFQPreview";
import { RfxSubmissionDataProvider } from "newui/features/Rfx/data-provider/RfxSubmissionDataProvider";
import { RfxDataProvider } from "newui/features/Rfx/data-provider/RfxDataProvider";
import { EvaluationPage } from "newui/features/Rfx/Evaluation";
import GetRfxSubmissionByCompany from "app/GetRfxSubmissionByCompany";
import { useCurrentCompany, useIsCurrentUserAdmin } from "hooks/auth";
import { CompanyProfile } from "newui/features/Company";
import { KeyPersonnelFormPage } from "newui/features/Company/KeyPersonnel";
import { EditUserProfile } from "components/EditUserProfile";
import { UserListPage } from "newui/features/Auth/components/User/UserListPage";
import { useCurrentUserHasFeatureFlag } from "hooks/featureFlags";
import { FeatureFlagType } from "newui/features/Auth/types/FeatureFlag.type";
import {
	ExecutiveViewBanner,
	useExecutiveStaleStateReconcile,
} from "newui/features/ExecutiveView";

export const SupplierComponent = ({
	getPastProjects,
	getResources,
	getDocuments,
	loading,
	path, // eslint-disable-line no-unused-vars
}) => {
	const profileContext = useProfileContext();
	const currentCompany = useCurrentCompany();
	const isAdminUser = useIsCurrentUserAdmin();
	useExecutiveStaleStateReconcile();
	useEffect(() => {
		//Set this company ID from the route variable - this allows anyone to access/edit all companies
		//When auth system is implemented, the getCompany action will be dispatched during login action,
		//and this data will be ready in the store

		const companyId = currentCompany?.id;

		if (companyId) {
			getPastProjects(companyId);
			getResources(companyId);
			getDocuments(companyId);
		}
	}, [currentCompany, getPastProjects, getDocuments, getResources]);

	return (
		<>
			{!loading ? (
				<Box
					display="flex"
					flexWrap="nowrap"
					width="100%"
					minH="100vh"
				>
					<AppMenu />
					<Box
						display="flex"
						flexDirection="column"
						flex="1"
						minH="0"
						minW="0"
						overflowX="hidden"
					>
						<ExecutiveViewBanner />

						<ContentContainer
							id="element-scroll"
							className="px-1.5"
						>
							<Breadcrumbs ignoreCompany={false} />
							<Router primary={false}>
								<MyAccount path="my-account" />

								{/* <CompanyProfile path="company-profile/*" company={company} /> */}
								{profileContext?.isRfqFeatureEnabled && (
									<>
										<GetRfxSubmissionByCompany
											companyId={currentCompany?.id}
											path="rfx/:rfxId/getsubmission/:submissionCompanyId/:currentTab"
										/>
										{/*
											NOTE: reach-router's <Redirect> resolves `to` relative
											to its OWN matched uri (the uri that `from` just
											matched), not the parent Router's basepath — so `to`
											must NOT repeat any segment already present in `from`,
											and must NOT start with a leading slash (that forces
											an app-root-absolute path, dropping the
											/account/:companyId prefix entirely), or the resolved
											path either doubles or loses its prefix.
										*/}
										{/*
											"all", not "current": the BIDDER Tenders & Quotes tabs
											are submission statuses (all/accepted/submitted/
											unsuccessful/awarded) and have no "current". Sending
											a bidder to /current landed on an invalid segment,
											which the shell silently coerces back to its default —
											so the URL and the visible tab disagreed. Approvals
											and Panel below are procurer-tabbed and DO use
											"current".
										*/}
										<Redirect
											from="/rfx"
											to="tenders-quotes/all"
											noThrow
										/>
										<Redirect
											from="/rfx/tenders-quotes"
											to="all"
											noThrow
										/>
										{/*
											Legacy flat bidder list paths (`SupplierPaths.rfx.*`,
											e.g. /rfx/accepted) — these were never registered
											routes here, so they 404'd; they now land on the
											equivalent tab of the consolidated list. Kept because
											they are the shape of every old bookmark and emailed
											link.
										*/}
										<Redirect
											from="/rfx/all"
											to="../tenders-quotes/all"
											noThrow
										/>
										<Redirect
											from="/rfx/accepted"
											to="../tenders-quotes/accepted"
											noThrow
										/>
										<Redirect
											from="/rfx/submitted"
											to="../tenders-quotes/submitted"
											noThrow
										/>
										<Redirect
											from="/rfx/unsuccessful"
											to="../tenders-quotes/unsuccessful"
											noThrow
										/>
										<Redirect
											from="/rfx/awarded"
											to="../tenders-quotes/awarded"
											noThrow
										/>
										<Redirect
											from="/rfx/approvals"
											to="current"
											noThrow
										/>
										<Redirect
											from="/rfx/panel"
											to="current"
											noThrow
										/>
										<RfxBidderTendersQuotesPage
											path="/rfx/tenders-quotes/:tab"
											companyId={currentCompany?.id}
										/>
										<RfxApprovalsPage
											path="/rfx/approvals/:tab"
											companyId={currentCompany?.id}
										/>
										<RfxPanelPage
											path="/rfx/panel/:tab"
											companyId={currentCompany?.id}
										/>
										<RfxReadOnlyPreviewPage path="/rfx/:rfxUuid/preview" />
										<RfxSubmissionDataProvider
											path="rfx/:rfxRouteId/submission/:rfxInviteRouteId"
											useParentRfxData={false}
										>
											<RFQPreview path=":currentTab" />
											<RFQPreview path=":currentTab/:contextId" />
											{/* <RFQPreview path="/*" /> */}
										</RfxSubmissionDataProvider>
										{/* Panel member RFx Evaluation */}
										<RfxDataProvider path="rfx/:rfxRouteId">
											<EvaluationPage path="evaluation/:evaluationId" />
										</RfxDataProvider>
									</>
								)}
								{/* Existing Route */}
								{/* START OF ACCOUNT SETTING MENU */}
								<Settings
									path="account-settings"
									company={currentCompany}
								/>
								<RfxSettings
									path="rfx-settings/*"
									company={currentCompany}
								/>
								<UserListPage path="account-settings/users" />
								{profileContext?.currentProfile ===
									CurrentAccountType.BIDDER && (
									<PdfSettingsPage path="pdf-settings" />
								)}
								<EditUserAccount path="edit-user-account" />
								<Invoice
									path="account-settings/invoice"
									company={currentCompany}
								/>
								<UpdateCreditCard path="account-settings/update-card-details" />
								{isAdminUser && (
									<RolesPermissionsPage path="account-settings/roles" />
								)}
								{isAdminUser && (
									<AuditTrailPage path="account-settings/audit" />
								)}

								<EditUserProfile
									path="edit-user-profile"
									company={"My Profile"}
								/>
								{/* END OF ACCOUNT SETTING MENU */}
								<CompanyProfile
									path="company-profile/:activeTab"
									company={currentCompany}
								/>
								<KeyPersonnelFormPage path="company-profile/:activeTab/add" />
								<KeyPersonnelFormPage path="company-profile/:activeTab/:id/edit" />
								<AddProject path="company-profile/:activeTab/create-project" />
								<EditProject
									path="company-profile/:activeTab/edit-project/:projectId"
									profileTypeId={currentCompany?.profile_type?.id}
								/>
								<DocumentRequest path="company-profile/:activeTab/request-view" />
								<Search
									path="search/*"
									title="Search"
								/>

								<NotFound default />
							</Router>
						</ContentContainer>
					</Box>
				</Box>
			) : (
				<div style={{ padding: 50, flexGrow: 1 }}>
					<Skeleton
						duration={0.5}
						count={10}
						width="100%"
					/>
				</div>
			)}
		</>
	);
};

const mapStateToProps = () => {
	return {
		loading: false, //removed because same redux state value is used in 2 different component
	};
};

export const Supplier = connect(mapStateToProps, {
	getPastProjects,
	getResources,
	getDocuments,
})(SupplierComponent);
